import '@/src/api/client';
import { client } from '@/src/api/client';
import { normalizeApiError, type ApiErrorDetails } from '@/src/api/error-utils';
import {
  contactCenterControllerArchiveConnector,
  contactCenterControllerAssignConversation,
  contactCenterControllerCloseConversation,
  contactCenterControllerCreateConnector,
  contactCenterControllerDeleteConversationMessage,
  contactCenterControllerGetConnector,
  contactCenterControllerGetConnectorWebhookSetup,
  contactCenterControllerGetConversation,
  contactCenterControllerLinkConversationContact,
  contactCenterControllerLinkConversationDeal,
  contactCenterControllerListChannels,
  contactCenterControllerListConnectors,
  contactCenterControllerListConversationMessages,
  contactCenterControllerListConversations,
  contactCenterControllerListMessageTemplates,
  contactCenterControllerListRealtimeEvents,
  contactCenterControllerMarkConversationRead,
  contactCenterControllerPickupConversation,
  contactCenterControllerRegenerateWebhookToken,
  contactCenterControllerReopenConversation,
  contactCenterControllerRestoreConnector,
  contactCenterControllerRetryConversationMessage,
  contactCenterControllerSendConversationMessage,
  contactCenterControllerSendConversationTemplateMessage,
  contactCenterControllerSyncGupshupConnectorTemplates,
  contactCenterControllerUpdateConnector,
  contactCenterControllerUploadConversationAttachment,
  facebookCommentsOAuthControllerCallback,
  facebookCommentsOAuthControllerComplete,
  facebookCommentsOAuthControllerReconnect,
  facebookCommentsOAuthControllerStart,
  facebookOAuthControllerCallback,
  facebookOAuthControllerComplete,
  facebookOAuthControllerReconnect,
  facebookOAuthControllerStart,
  gmailOAuthControllerComplete,
  gmailOAuthControllerReconnect,
  gmailOAuthControllerStart,
  instagramCommentsOAuthControllerCallback,
  instagramCommentsOAuthControllerComplete,
  instagramCommentsOAuthControllerReconnect,
  instagramCommentsOAuthControllerStart,
  instagramOAuthControllerCallback,
  instagramOAuthControllerComplete,
  instagramOAuthControllerReconnect,
  instagramOAuthControllerStart,
  telegramConnectorAdminControllerCheckBusinessConnection,
  tiktokBusinessOAuthControllerComplete,
  tiktokBusinessOAuthControllerStart,
} from '@/src/api/generated/sdk.gen';
import type {
  DepartmentSummaryDto,
  ContactCenterChannelDto,
  ContactCenterConnectorDto,
  ContactCenterConnectorResponseDto,
  ContactCenterControllerListConversationsData,
  ContactCenterConversationMessageListResponseDto,
  ContactCenterConversationMessagePageDto,
  ContactCenterConversationResponseDto,
  ContactCenterMessageTemplateDto,
  ContactCenterRealtimeEventListResponseDto,
  CreateContactCenterConnectorRequestDto,
  TiktokBusinessOAuthStartResponseDto,
} from '@/src/api/generated/types.gen';
import { downloadAuthenticatedFile } from '@/src/api/raw-client';
import { getBearerToken } from '@/src/auth/token-storage';
import {
  connectorChannelCapabilities,
  connectorProviderSecret,
  connectorRequestSettings,
  connectorSettingsFromRecord,
  customWebhookChannelType,
  defaultConnectorSettings,
  historySyncStatusFromValue,
} from '@/src/integrations/contact-center/connectors/feature-adapter';
import {
  syncConnectorMessageHistory as syncContactCenterConnectorHistory,
  syncConversationMessageHistory as syncContactCenterConversationHistory,
} from '@/src/integrations/contact-center/connectors/history-sync-api';

import type {
  ContactCenterCatalog,
  ContactCenterChannel,
  ContactCenterChannelType,
  ContactCenterConnector,
  ContactCenterConnectorDraft,
  ContactCenterConnectorEditorWorkspace,
  ContactCenterConversationDetail,
  ContactCenterConversationFilters,
  ContactCenterConversationList,
  ContactCenterInboxWorkspace,
  ContactCenterConversationMessage,
  ContactCenterConversationMessageList,
  ContactCenterGmailOAuthStartResponse,
  ContactCenterMessageTemplate,
  ContactCenterMetaOAuthCallbackResponse,
  ContactCenterMetaOAuthKind,
  ContactCenterMetaOAuthStartResponse,
  ContactCenterSecretStatus,
  ContactCenterSendMessageRequest,
  ContactCenterUploadedAttachment,
} from './types';

export type ContactCenterApiResult<T> = { data: T | null; error: ApiErrorDetails | null };

type ApiResult<T> = { data?: T; error?: unknown };

type ContactCenterConnectorEditorWorkspaceSectionErrorDto = {
  section: 'DEPARTMENT_OPTIONS' | 'WEBHOOK_SETUP' | 'TEMPLATES';
  code: 'SECTION_UNAVAILABLE' | 'LIMIT_REACHED';
  message: string;
};

type ContactCenterConnectorEditorWorkspaceResponseDto = {
  connector: ContactCenterConnectorDto;
  eligibleDepartments?: DepartmentSummaryDto[];
  webhookSetup?: { webhookVerifyToken: string | null };
  templates?: ContactCenterMessageTemplateDto[];
  sectionErrors: ContactCenterConnectorEditorWorkspaceSectionErrorDto[];
};

type ContactCenterConversationListQuery = NonNullable<ContactCenterControllerListConversationsData['query']> & { cursor?: string; dealId?: string; connectorType?: string; connectorTypes?: string[]; pipelineIds?: string[]; queueView?: ContactCenterConversationFilters['queueView'] };

export function normalizeContactCenterApiResult<T>(result: ApiResult<T>): ContactCenterApiResult<T> {
  if (result.data !== undefined) {
    return { data: result.data, error: null };
  }

  return { data: null, error: normalizeApiError(result.error, 'Contact Center request failed.') };
}

function normalizeDeliveryFailure(value: unknown): ContactCenterConversationMessage['deliveryFailure'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  return typeof record.category === 'string' && typeof record.message === 'string'
    ? { category: record.category, message: record.message }
    : null;
}

function normalizeConversationConnectorType(value: string | null | undefined): string | null {
  return value?.trim() || null;
}

function stringFromRecord(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function templateMetadata(template: ContactCenterMessageTemplateDto): Record<string, unknown> {
  const value = (template as ContactCenterMessageTemplateDto & { metadata?: unknown }).metadata;
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function templateProviderStatus(template: ContactCenterMessageTemplateDto, metadata: Record<string, unknown>): string | null {
  return stringFromRecord(template as unknown as Record<string, unknown>, 'providerStatus')
    ?? stringFromRecord(metadata, 'providerStatus')
    ?? stringFromRecord(metadata, 'approvalStatus')
    ?? stringFromRecord(metadata, 'status');
}

function templateProviderId(template: ContactCenterMessageTemplateDto, metadata: Record<string, unknown>): string | null {
  return stringFromRecord(template as unknown as Record<string, unknown>, 'providerTemplateId')
    ?? stringFromRecord(metadata, 'providerTemplateId')
    ?? stringFromRecord(metadata, 'providerTemplateName')
    ?? stringFromRecord(metadata, 'templateId')
    ?? template.connectorTemplateName;
}

function templateLastSyncedAt(template: ContactCenterMessageTemplateDto, metadata: Record<string, unknown>): string | null {
  return stringFromRecord(template as unknown as Record<string, unknown>, 'lastSyncedAt')
    ?? stringFromRecord(metadata, 'lastSyncedAt')
    ?? stringFromRecord(metadata, 'syncedAt');
}

function normalizeConversationDeviceDisplayName(value: unknown): string | null {
  return typeof value === 'string' ? value.trim() || null : null;
}

function normalizeConversationMessage(value: unknown): ContactCenterConversationMessage | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.id !== 'string' || typeof record.direction !== 'string' || typeof record.deliveryStatus !== 'string' || typeof record.occurredAt !== 'string' || typeof record.createdAt !== 'string') return null;
  return {
    id: record.id,
    direction: record.direction === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND',
    senderType: typeof record.senderType === 'string' ? record.senderType : 'SYSTEM',
    senderDisplayName: typeof record.senderDisplayName === 'string' ? record.senderDisplayName : null,
    body: typeof record.body === 'string' ? record.body : null,
    isDeleted: record.isDeleted === true,
    messageKind: record.messageKind === 'TEXT' || record.messageKind === 'MEDIA' || record.messageKind === 'SYSTEM' || record.messageKind === 'EMPTY'
      ? record.messageKind
      : Array.isArray(record.attachments) && record.attachments.length > 0
        ? 'MEDIA'
        : typeof record.body === 'string' && record.body.trim().length > 0
          ? 'TEXT'
          : 'EMPTY',
    deliveryFailure: normalizeDeliveryFailure(record.deliveryFailure),
    deliveryStatus: record.deliveryStatus,
    occurredAt: record.occurredAt,
    createdAt: record.createdAt,
    externalMessageId: typeof record.externalMessageId === 'string' ? record.externalMessageId : null,
    attachments: Array.isArray(record.attachments) ? record.attachments as ContactCenterConversationMessage['attachments'] : [],
  };
}

function normalizeConversationMessagePage(value: unknown): ContactCenterConversationMessageList['page'] | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const pageRecord = value as Record<string, unknown>;
  return {
    oldestCursor: typeof pageRecord.oldestCursor === 'string' ? pageRecord.oldestCursor : null,
    newestCursor: typeof pageRecord.newestCursor === 'string' ? pageRecord.newestCursor : null,
    hasOlder: pageRecord.hasOlder === true,
    hasNewer: pageRecord.hasNewer === true,
    limit: typeof pageRecord.limit === 'number' ? pageRecord.limit : 50,
  };
}

function normalizeConversationMessageList(value: unknown): ContactCenterConversationMessageList | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const page = record.page;
  if (!Array.isArray(record.messages) || !page || typeof page !== 'object' || Array.isArray(page)) return null;
  const messages = record.messages.map(normalizeConversationMessage);
  if (messages.some((message) => message === null)) return null;
  const normalizedPage = normalizeConversationMessagePage(page);
  if (!normalizedPage) return null;
  return {
    messages: messages as ContactCenterConversationMessage[],
    page: normalizedPage,
  };
}

function isContactCenterAssigneeSummary(value: unknown): value is ContactCenterConversationDetail['assignees'][number] {
  return Boolean(value)
    && typeof value === 'object'
    && typeof (value as Record<string, unknown>).tenantMembershipId === 'string'
    && typeof (value as Record<string, unknown>).userId === 'string'
    && typeof (value as Record<string, unknown>).email === 'string'
    && typeof (value as Record<string, unknown>).displayName === 'string';
}

function normalizeAssigneeList(value: unknown): ContactCenterConversationDetail['assignees'] {
  return Array.isArray(value) ? value.filter(isContactCenterAssigneeSummary) : [];
}

function normalizeAssignees(conversation: ContactCenterConversationResponseDto['conversation']): ContactCenterConversationDetail['assignees'] {
  const assignees = normalizeAssigneeList((conversation as Record<string, unknown>).assignees);
  return assignees.length ? assignees : conversation.assignee ? [conversation.assignee] : [];
}

function normalizeConversationRecord(
  conversation: ContactCenterConversationResponseDto['conversation'],
  fallbackMessage: string,
): ContactCenterApiResult<ContactCenterConversationDetail> {
  if (!conversation.actions) {
    return normalizeContactCenterApiResult({ error: 'Conversation actions are unavailable.' });
  }

  const connectorType = normalizeConversationConnectorType(conversation.connectorType);
  const deviceDisplayName = normalizeConversationDeviceDisplayName((conversation as Record<string, unknown>).deviceDisplayName);
  const assignees = normalizeAssignees(conversation);
  const assignedTenantMembershipIds = Array.isArray((conversation as Record<string, unknown>).assignedTenantMembershipIds)
    ? ((conversation as Record<string, unknown>).assignedTenantMembershipIds as unknown[]).filter((id): id is string => typeof id === 'string')
    : assignees.map((assignee) => assignee.tenantMembershipId);
  const assignableUsers = normalizeAssigneeList((conversation as Record<string, unknown>).assignableUsers);
  const normalizedMessages = Array.isArray((conversation as Record<string, unknown>).messages)
    ? ((conversation as Record<string, unknown>).messages as unknown[]).map(normalizeConversationMessage).filter((message): message is ContactCenterConversationMessage => message !== null)
    : [];
  const messagePage = normalizeConversationMessagePage((conversation as Record<string, unknown>).messagePage);

  const settingsRecord = (conversation as Record<string, unknown>).settings;
  const settings = settingsRecord && typeof settingsRecord === 'object' && !Array.isArray(settingsRecord)
    ? { ...settingsRecord, ...(connectorType ? { connectorType } : {}) }
    : connectorType ? { connectorType } : null;

  return normalizeContactCenterApiResult({
    data: {
      ...conversation,
      messages: normalizedMessages,
      messagesPage: messagePage,
      connectorType,
      deviceDisplayName,
      settings,
      actions: conversation.actions,
      assignedTenantMembershipIds,
      assignees,
      assignableUsers,
    },
  });
}

function normalizeConversationResponse(
  result: ApiResult<ContactCenterConversationResponseDto>,
  fallbackMessage: string,
): ContactCenterApiResult<ContactCenterConversationDetail> {
  if (result.error) {
    return normalizeContactCenterApiResult({ error: result.error });
  }

  const conversation = result.data?.conversation;
  if (!conversation) {
    return normalizeContactCenterApiResult({ error: { message: fallbackMessage } });
  }

  return normalizeConversationRecord(conversation, fallbackMessage);
}

function channelCapabilities(type: ContactCenterChannelType) {
  return connectorChannelCapabilities(type);
}

function toChannel(channel: ContactCenterChannelDto, connectors: ContactCenterConnectorDto[]): ContactCenterChannel {
  const fallbackCapabilities = channelCapabilities(channel.type);
  return {
    id: channel.id,
    type: channel.type,
    name: channel.name,
    description: channel.description,
    status: channel.status ?? fallbackCapabilities.status,
    supportsInbound: channel.supportsInbound ?? fallbackCapabilities.supportsInbound,
    supportsOutbound: channel.supportsOutbound ?? fallbackCapabilities.supportsOutbound,
    supportsOAuth: channel.supportsOAuth ?? fallbackCapabilities.supportsOAuth,
    supportsWebhookSecret: channel.supportsWebhookSecret ?? fallbackCapabilities.supportsWebhookSecret,
    connectorCount: connectors.filter((connector) => connector.channelId === channel.id).length,
  };
}

const defaultProviderSecretStatus: ContactCenterSecretStatus = {
  configured: false,
  apiKeyConfigured: false,
  updatedAt: null,
  lastRotatedAt: null,
  keyVersion: null,
};

function providerSecretStatus(status: ContactCenterConnectorDto['providerSecretStatus']): ContactCenterSecretStatus {
  const normalizedStatus = {
    ...defaultProviderSecretStatus,
    ...(status ?? {}),
  };
  const apiKeyConfigured = (normalizedStatus as ContactCenterConnectorDto['providerSecretStatus'] & { apiKeyConfigured?: boolean }).apiKeyConfigured;
  return { ...normalizedStatus, apiKeyConfigured: apiKeyConfigured ?? normalizedStatus.configured };
}

function toConnector(connector: ContactCenterConnectorDto, channelById: Map<string, ContactCenterChannel>): ContactCenterConnector {
  const channel = channelById.get(connector.channelId);
  const settings = connector.settings;
  const connectorHealth = connector as ContactCenterConnectorDto & {
    lastInboundAt?: string | null;
    lastHealthStatus?: string | null;
    lastErrorCode?: string | null;
    lastErrorMessage?: string | null;
  };
  return {
    id: connector.id,
    channelId: connector.channelId,
    channelType: channel?.type ?? customWebhookChannelType(),
    channelName: channel?.name ?? 'Unknown channel',
    name: connector.name,
    status: connector.lifecycleState,
    providerSecretStatus: providerSecretStatus(connector.providerSecretStatus),
    webhookVerifyToken: connector.webhookVerifyToken ?? null,
    historySync: historySyncStatusFromValue((connector as ContactCenterConnectorDto & { historySync?: unknown }).historySync),
    settings: connectorSettingsFromRecord(settings, {
      autoCreateContact: connector.autoCreateContact,
      updateExistingContact: connector.updateExistingContact,
      placeholderContactsAllowed: connector.allowPlaceholderContact,
      autoCreateDeal: connector.autoCreateDeal,
      createDealMode: connector.autoCreateDealMode,
      assignmentMode: connector.assignmentMode,
      defaultPipelineId: connector.defaultPipelineId,
      defaultStageId: connector.defaultStageId,
      defaultOwnerTenantMembershipId: connector.defaultOwnerTenantMembershipId,
      defaultAssigneeTenantMembershipId: connector.defaultAssigneeTenantMembershipId,
    }, channel?.type),
    eligibleTenantMembershipIds: connector.eligibleTenantMembershipIds ?? [],
    eligibleUsers: connector.eligibleUsers ?? [],
    lastInboundAt: connectorHealth.lastInboundAt ?? null,
    lastHealthStatus: connectorHealth.lastHealthStatus ?? null,
    lastErrorCode: connectorHealth.lastErrorCode ?? null,
    lastErrorMessage: connectorHealth.lastErrorMessage ?? null,
    updatedAt: connector.updatedAt,
  };
}
function toMessageTemplate(template: ContactCenterMessageTemplateDto): ContactCenterMessageTemplate {
  const metadata = templateMetadata(template);
  const missingProviderFields = [
    template.connectorTemplateName ? null : 'provider template id',
    template.languageCode ? null : 'language',
    template.body ? null : 'body',
  ].filter((field): field is string => Boolean(field));

  return {
    id: template.id,
    channelId: template.channelId,
    connectorId: template.connectorId,
    name: template.name,
    displayName: template.displayName,
    connectorTemplateName: template.connectorTemplateName,
    languageCode: template.languageCode,
    status: template.status,
    body: template.body,
    variables: template.variables,
    providerStatus: templateProviderStatus(template, metadata),
    providerTemplateId: templateProviderId(template, metadata),
    lastSyncedAt: templateLastSyncedAt(template, metadata),
    missingProviderFields,
  };
}

async function getChannelByIdMap(connectorDtos: ContactCenterConnectorDto[] = []) {
  const channelResult = await contactCenterControllerListChannels();

  if (channelResult.error) {
    return normalizeContactCenterApiResult<Map<string, ContactCenterChannel>>({ error: channelResult.error });
  }

  const channels = (channelResult.data?.channels ?? []).map((channel) => toChannel(channel, connectorDtos));
  return normalizeContactCenterApiResult({ data: new Map(channels.map((channel) => [channel.id, channel])) });
}

export { providerApiBaseUrl as contactCenterConnectorApiBaseUrl, providerApiBaseUrl as contactCenterProviderApiBaseUrl } from '@/src/integrations/contact-center/connectors/feature-adapter';


export function buildDefaultContactCenterConnectorDraft(channelId: string): ContactCenterConnectorDraft {
  return {
    channelId,
    name: '',
    webhookSecret: '',
    providerSecret: { whatsappApiKey: '', webhookSecret: '', apiKey: '' },
    settings: defaultConnectorSettings(),
    eligibleTenantMembershipIds: [],
  };
}

function connectorRequest(draft: ContactCenterConnectorDraft): CreateContactCenterConnectorRequestDto {
  const settings = draft.settings;
  const providerSecret = connectorProviderSecret(draft);

  return {
    channelId: draft.channelId,
    name: draft.name.trim(),
    autoCreateContact: settings.autoCreateContact,
    updateExistingContact: settings.updateExistingContact,
    allowPlaceholderContact: settings.placeholderContactsAllowed,
    autoCreateDeal: settings.autoCreateDeal,
    autoCreateDealMode: settings.createDealMode,
    defaultPipelineId: settings.defaultPipelineId,
    defaultStageId: settings.defaultStageId,
    defaultOwnerTenantMembershipId: settings.defaultOwnerTenantMembershipId,
    defaultAssigneeTenantMembershipId: settings.defaultAssigneeTenantMembershipId,
    assignmentMode: settings.assignmentMode,
    eligibleTenantMembershipIds: draft.eligibleTenantMembershipIds,
    settings: connectorRequestSettings(settings),
    ...(draft.webhookSecret.trim() ? { secret: draft.webhookSecret.trim() } : {}),
    ...(providerSecret ? { providerSecret } : {}),
  };
}

export function buildContactCenterConnectorRequestForTest(draft: ContactCenterConnectorDraft): CreateContactCenterConnectorRequestDto {
  return connectorRequest(draft);
}

type ContactCenterCompactConnectorPage = {
  connectors: ContactCenterConnectorDto[];
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
};

function queryString(query: Record<string, string | number | null | undefined>) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
}

export async function listContactCenterChannels(): Promise<ContactCenterApiResult<ContactCenterCatalog>> {
  const channelResult = await contactCenterControllerListChannels();
  if (channelResult.error) return normalizeContactCenterApiResult({ error: channelResult.error });
  const channels = (channelResult.data?.channels ?? []).map((channel) => toChannel(channel, []));
  return normalizeContactCenterApiResult({ data: { channels, connectors: [] } });
}

export async function listContactCenterCompactConnectors(
  channels: ContactCenterChannel[],
  query: { page: number; pageSize: number; pipelineId?: string; variant?: string },
): Promise<ContactCenterApiResult<{ connectors: ContactCenterConnector[]; page: number; pageSize: number; total: number; pageCount: number }>> {
  const response = await client.get<ContactCenterCompactConnectorPage, unknown, false>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: `/contact-center/connectors/compact${queryString(query)}`,
  }) as ApiResult<ContactCenterCompactConnectorPage>;
  if (response.error || !response.data) return normalizeContactCenterApiResult({ error: response.error });
  const channelById = new Map(channels.map((channel) => [channel.id, channel]));
  return normalizeContactCenterApiResult({
    data: {
      ...response.data,
      connectors: response.data.connectors.map((connector) => toConnector(connector, channelById)),
    },
  });
}

export async function getContactCenterConnector(id: string): Promise<ContactCenterApiResult<ContactCenterConnector>> {
  const [connectorResult, channelsResult] = await Promise.all([
    contactCenterControllerGetConnector({ path: { id } }),
    contactCenterControllerListChannels(),
  ]);
  if (connectorResult.error) return normalizeContactCenterApiResult({ error: connectorResult.error });
  if (channelsResult.error) return normalizeContactCenterApiResult({ error: channelsResult.error });
  const connector = connectorResult.data?.connector;
  if (!connector) return normalizeContactCenterApiResult({ error: { message: 'Connector could not be loaded.' } });
  const channels = (channelsResult.data?.channels ?? []).map((channel) => toChannel(channel, [connector]));
  return normalizeContactCenterApiResult({ data: toConnector(connector, new Map(channels.map((channel) => [channel.id, channel]))) });
}

export async function getContactCenterConnectorEditorWorkspace(
  id: string,
  channels: ContactCenterChannel[],
): Promise<ContactCenterApiResult<ContactCenterConnectorEditorWorkspace>> {
  // Temporary typed wrapper for #527. The integration owner regenerates the
  // generated client after all #522 workspace slices are merged.
  const response = await client.get<ContactCenterConnectorEditorWorkspaceResponseDto, unknown, false>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: `/contact-center/connectors/${encodeURIComponent(id)}/editor-workspace`,
  }) as ApiResult<ContactCenterConnectorEditorWorkspaceResponseDto>;
  if (response.error || !response.data?.connector) {
    return normalizeContactCenterApiResult({
      error: response.error ?? { message: 'Connector workspace could not be loaded.' },
    });
  }

  const channelById = new Map(channels.map((channel) => [channel.id, channel]));
  const workspace = response.data;
  return normalizeContactCenterApiResult({
    data: {
      connector: toConnector(workspace.connector, channelById),
      eligibleDepartments: workspace.eligibleDepartments ?? [],
      webhookSetup: workspace.webhookSetup ?? null,
      templates: (workspace.templates ?? []).map((template) => toMessageTemplate(template)),
      sectionErrors: workspace.sectionErrors,
    },
  });
}

export async function listContactCenterCatalog(): Promise<ContactCenterApiResult<ContactCenterCatalog>> {
  const [channelResult, connectorResult] = await Promise.all([
    contactCenterControllerListChannels(),
    contactCenterControllerListConnectors(),
  ]);

  if (channelResult.error) {
    return normalizeContactCenterApiResult({ error: channelResult.error });
  }

  if (connectorResult.error) {
    return normalizeContactCenterApiResult({ error: connectorResult.error });
  }

  const connectorDtos = connectorResult.data?.connectors ?? [];
  const channels = (channelResult.data?.channels ?? []).map((channel) => toChannel(channel, connectorDtos));
  const channelById = new Map(channels.map((channel) => [channel.id, channel]));
  const connectors = connectorDtos.map((connector) => toConnector(connector, channelById));

  return normalizeContactCenterApiResult({ data: { channels, connectors } });
}

export async function getContactCenterConversation(id: string): Promise<ContactCenterApiResult<ContactCenterConversationDetail>> {
  const result = await contactCenterControllerGetConversation({ path: { conversationId: id } });

  return normalizeConversationResponse(result, 'Conversation could not be loaded.');
}

type ContactCenterConversationWorkspaceConversation = Omit<
  ContactCenterConversationResponseDto['conversation'],
  'messages' | 'messagePage'
>;
type ContactCenterConversationWorkspaceResponse = {
  conversation: ContactCenterConversationWorkspaceConversation;
  messages: ContactCenterConversationMessageListResponseDto['messages'] | null;
  messagePage: ContactCenterConversationMessagePageDto | null;
  templates: ContactCenterMessageTemplateDto[] | null;
  sectionErrors: Array<{
    section: 'MESSAGES' | 'TEMPLATES';
    code: 'UNAVAILABLE';
    message: string;
  }>;
};

export type ContactCenterConversationWorkspace = {
  conversation: ContactCenterConversationDetail;
  templates: ContactCenterMessageTemplate[] | null;
  sectionErrors: ContactCenterConversationWorkspaceResponse['sectionErrors'];
};

// The generated client is regenerated by the integration slice. Keep this
// branch typed at the wrapper boundary instead of editing generated files.
export async function getContactCenterConversationWorkspace(
  id: string,
): Promise<ContactCenterApiResult<ContactCenterConversationWorkspace>> {
  const response = await client.get<ContactCenterConversationWorkspaceResponse, unknown, false>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: `/contact-center/conversations/${encodeURIComponent(id)}/workspace`,
  }) as ApiResult<ContactCenterConversationWorkspaceResponse>;

  if (response.error || !response.data?.conversation) {
    return normalizeContactCenterApiResult({
      error: response.error ?? { message: 'Conversation workspace could not be loaded.' },
    });
  }

  const raw = response.data;
  const conversationResult = normalizeConversationRecord({
    ...raw.conversation,
    messages: raw.messages ?? [],
    messagePage: raw.messagePage as ContactCenterConversationMessagePageDto,
  }, 'Conversation workspace could not be loaded.');
  if (conversationResult.error || !conversationResult.data) {
    return normalizeContactCenterApiResult({
      error: conversationResult.error ?? { message: 'Conversation workspace could not be loaded.' },
    });
  }

  return normalizeContactCenterApiResult({
    data: {
      conversation: conversationResult.data,
      templates:
        raw.templates === null
          ? null
          : raw.templates.map((template) => toMessageTemplate(template)),
      sectionErrors: raw.sectionErrors,
    },
  });
}

export async function listContactCenterConversationMessages(
  id: string,
  options: { limit?: number; before?: string | null } = {},
): Promise<ContactCenterApiResult<ContactCenterConversationMessageList>> {
  const response = await contactCenterControllerListConversationMessages({
    path: { conversationId: id },
    query: {
      ...(options.limit ? { limit: options.limit } : {}),
      ...(options.before ? { before: options.before } : {}),
    },
  });
  if (response.error) {
    return normalizeContactCenterApiResult({ error: response.error });
  }
  const data = normalizeConversationMessageList(response.data);
  return data
    ? normalizeContactCenterApiResult({ data })
    : normalizeContactCenterApiResult({ error: { message: 'Conversation messages returned an invalid response.' } });
}

function normalizeConversationSummary<T extends { assignee?: ContactCenterConversationDetail['assignee']; assignedTenantMembershipId?: string | null }>(conversation: T): T & Pick<ContactCenterConversationDetail, 'assignedTenantMembershipIds' | 'assignees' | 'connectorType' | 'deviceDisplayName'> {
  const record = conversation as Record<string, unknown>;
  const assignees = normalizeAssigneeList(record.assignees);
  const normalizedAssignees = assignees.length ? assignees : conversation.assignee ? [conversation.assignee] : [];
  const assignedTenantMembershipIds = Array.isArray(record.assignedTenantMembershipIds)
    ? (record.assignedTenantMembershipIds as unknown[]).filter((id): id is string => typeof id === 'string')
    : normalizedAssignees.map((assignee) => assignee.tenantMembershipId);
  const connectorType = normalizeConversationConnectorType(typeof record.connectorType === 'string' ? record.connectorType : null);
  const deviceDisplayName = normalizeConversationDeviceDisplayName(record.deviceDisplayName);

  return { ...conversation, connectorType, deviceDisplayName, assignedTenantMembershipIds, assignees: normalizedAssignees };
}

function conversationListQuery(
  filters: ContactCenterConversationFilters,
): ContactCenterConversationListQuery {
  const query: ContactCenterConversationListQuery = {};
  if (filters.status) query.status = filters.status;
  if (filters.queueView) query.queueView = filters.queueView;
  if (filters.connectorId) query.connectorId = filters.connectorId;
  if (filters.connectorType) query.connectorType = filters.connectorType;
  if (filters.connectorTypes?.length) query.connectorTypes = filters.connectorTypes;
  if (filters.channelId) query.channelId = filters.channelId;
  if (filters.dealId) query.dealId = filters.dealId;
  if (filters.pipelineIds?.length) query.pipelineIds = filters.pipelineIds;
  if (filters.hasUnread !== undefined) query.hasUnread = filters.hasUnread;
  if (filters.slaState) query.slaState = filters.slaState;
  if (filters.search?.trim()) query.search = filters.search.trim();
  if (filters.cursor) query.cursor = filters.cursor;
  if (filters.page) query.page = filters.page;
  if (filters.pageSize) query.pageSize = filters.pageSize;
  return query;
}

function conversationQueryString(
  query: ContactCenterConversationListQuery,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, item));
    } else {
      params.set(key, String(value));
    }
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
}

export async function getContactCenterInboxWorkspace(
  filters: ContactCenterConversationFilters = {},
): Promise<ContactCenterApiResult<ContactCenterInboxWorkspace>> {
  const response = await client.get<ContactCenterInboxWorkspace, unknown, false>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: `/contact-center/inbox/workspace${conversationQueryString(conversationListQuery(filters))}`,
    ...(filters.requestSignal ? { signal: filters.requestSignal } : {}),
  }) as ApiResult<ContactCenterInboxWorkspace>;
  if (response.error || !response.data) {
    return normalizeContactCenterApiResult<ContactCenterInboxWorkspace>({
      error: response.error ?? { message: 'Contact Center inbox could not be loaded.' },
    });
  }

  const pageRecord = response.data as typeof response.data & {
    nextCursor?: unknown;
    hasMore?: unknown;
  };

  return normalizeContactCenterApiResult({
    data: {
      ...response.data,
      conversations: response.data.conversations.map((conversation) => normalizeConversationSummary(conversation)),
      nextCursor: typeof pageRecord.nextCursor === 'string' ? pageRecord.nextCursor : null,
      hasMore: pageRecord.hasMore === true,
    },
  });
}

export async function listContactCenterConversations(
  filters: ContactCenterConversationFilters = {},
): Promise<ContactCenterApiResult<ContactCenterConversationList>> {
  const query = conversationListQuery(filters);

  const result = await contactCenterControllerListConversations({
    query,
    ...(filters.requestSignal ? { signal: filters.requestSignal } : {}),
  });

  if (result.error || !result.data) {
    return normalizeContactCenterApiResult<ContactCenterConversationList>({ error: result.error ?? { message: 'Conversations could not be loaded.' } });
  }

  const pageRecord = result.data as typeof result.data & { nextCursor?: unknown; hasMore?: unknown };

  return normalizeContactCenterApiResult({
    data: {
      ...result.data,
      conversations: result.data.conversations.map((conversation) => normalizeConversationSummary(conversation)),
      nextCursor: typeof pageRecord.nextCursor === 'string' ? pageRecord.nextCursor : null,
      hasMore: pageRecord.hasMore === true,
    },
  });
}

export async function pickupContactCenterConversation(id: string): Promise<ContactCenterApiResult<ContactCenterConversationDetail>> {
  const result = await contactCenterControllerPickupConversation({ path: { conversationId: id } });

  return normalizeConversationResponse(result, 'Conversation could not be picked up.');
}

async function updateContactCenterConversationLifecycle(
  id: string,
  action: 'close' | 'reopen',
): Promise<ContactCenterApiResult<ContactCenterConversationDetail>> {
  const result = action === 'close'
    ? await contactCenterControllerCloseConversation({ path: { conversationId: id } })
    : await contactCenterControllerReopenConversation({ path: { conversationId: id } });

  return normalizeConversationResponse(result, `Conversation could not be ${action === 'close' ? 'closed' : 'reopened'}.`);
}

export async function closeContactCenterConversation(id: string): Promise<ContactCenterApiResult<ContactCenterConversationDetail>> {
  return updateContactCenterConversationLifecycle(id, 'close');
}

export async function reopenContactCenterConversation(id: string): Promise<ContactCenterApiResult<ContactCenterConversationDetail>> {
  return updateContactCenterConversationLifecycle(id, 'reopen');
}

export async function markContactCenterConversationRead(id: string): Promise<ContactCenterApiResult<ContactCenterConversationDetail>> {
  const result = await contactCenterControllerMarkConversationRead({ path: { conversationId: id } });

  return normalizeConversationResponse(result, 'Conversation could not be marked read.');
}

export async function uploadContactCenterConversationAttachment(
  id: string,
  file: File,
): Promise<ContactCenterApiResult<ContactCenterUploadedAttachment>> {
  const result = await contactCenterControllerUploadConversationAttachment({
    body: { file },
    headers: { 'Content-Type': null },
    path: { conversationId: id },
  });

  return normalizeContactCenterApiResult({ data: result.data, error: result.error });
}

export async function sendContactCenterConversationMessage(
  id: string,
  request: ContactCenterSendMessageRequest,
): Promise<ContactCenterApiResult<ContactCenterConversationDetail>> {
  const body = {
    ...(request.body?.trim() ? { body: request.body.trim() } : {}),
    ...(request.attachmentIds?.length ? { attachmentIds: request.attachmentIds } : {}),
  };

  const result = await contactCenterControllerSendConversationMessage({
    body,
    path: { conversationId: id },
  });

  return normalizeConversationResponse(result, 'Message could not be sent.');
}

export async function retryContactCenterConversationMessage(
  conversationId: string,
  messageId: string,
): Promise<ContactCenterApiResult<ContactCenterConversationDetail>> {
  const result = await contactCenterControllerRetryConversationMessage({ path: { conversationId, messageId } });

  return normalizeConversationResponse(result, 'Message could not be retried.');
}

export async function deleteContactCenterConversationMessage(
  conversationId: string,
  messageId: string,
): Promise<ContactCenterApiResult<ContactCenterConversationDetail>> {
  const response = await contactCenterControllerDeleteConversationMessage({
    path: { conversationId, messageId },
  });
  return normalizeConversationResponse(response, 'Message could not be deleted.');
}

export async function listContactCenterRealtimeEvents({
  after,
  conversationId,
  limit,
}: {
  after?: string | null;
  conversationId?: string;
  limit?: number;
} = {}): Promise<ContactCenterApiResult<ContactCenterRealtimeEventListResponseDto>> {
  const result = await contactCenterControllerListRealtimeEvents({
    query: {
      ...(after ? { after } : {}),
      ...(conversationId ? { conversationId } : {}),
      ...(limit ? { limit } : {}),
    },
  });

  return normalizeContactCenterApiResult(result);
}

export { syncContactCenterConnectorHistory, syncContactCenterConversationHistory, syncContactCenterConnectorHistory as syncWhatsAppDeviceHistory };

export async function downloadContactCenterAttachment(attachmentId: string, signal?: AbortSignal): Promise<ContactCenterApiResult<{ blob: Blob; fileName: string }>> {
  const result = await downloadAuthenticatedFile(
    `/contact-center/attachments/${encodeURIComponent(attachmentId)}/download`,
    { fallbackFileName: `contact-center-attachment-${attachmentId}`, signal },
  );
  return result.error
    ? normalizeContactCenterApiResult({ error: result.error })
    : normalizeContactCenterApiResult({ data: { blob: result.blob, fileName: result.fileName } });
}

export async function sendContactCenterConversationTemplateMessage(
  id: string,
  templateId: string,
  variables: Record<string, string>,
): Promise<ContactCenterApiResult<ContactCenterConversationDetail>> {
  const result = await contactCenterControllerSendConversationTemplateMessage({
    body: { templateId, variables },
    path: { conversationId: id },
  });

  return normalizeConversationResponse(result, 'Template message could not be sent.');
}

export async function listContactCenterMessageTemplates(input: {
  channelId?: string;
  connectorId?: string | null;
  includeGlobal?: boolean;
  status?: ContactCenterMessageTemplate['status'];
} = {}): Promise<ContactCenterApiResult<ContactCenterMessageTemplate[]>> {
  const requestedScopes = input.includeGlobal && input.connectorId
    ? [null, input.connectorId]
    : [input.connectorId ?? null];
  const results = await Promise.all(requestedScopes.map((connectorId) => contactCenterControllerListMessageTemplates({
    query: {
      ...(input.channelId ? { channelId: input.channelId } : {}),
      ...(connectorId ? { connectorId } : {}),
      ...(input.status ? { status: input.status } : {}),
      pageSize: 100,
    },
  })));

  const firstError = results.find((result) => result.error)?.error;
  if (firstError) {
    return normalizeContactCenterApiResult({ error: firstError });
  }

  const templatesById = new Map<string, ContactCenterMessageTemplate>();
  for (const result of results) {
    for (const template of result.data?.templates ?? []) {
      if (template.connectorId === null || template.connectorId === input.connectorId) {
        templatesById.set(template.id, toMessageTemplate(template));
      }
    }
  }

  return normalizeContactCenterApiResult({ data: [...templatesById.values()] });
}

function normalizeMessageTemplateListResponse(value: unknown): ContactCenterMessageTemplate[] | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const templates = Array.isArray(record.templates)
    ? record.templates
    : record.template && typeof record.template === 'object' && !Array.isArray(record.template)
      ? [record.template]
      : null;

  if (!templates) return null;
  return templates.map((template) => toMessageTemplate(template as ContactCenterMessageTemplateDto));
}

export async function syncContactCenterConnectorTemplates(connectorId: string): Promise<ContactCenterApiResult<ContactCenterMessageTemplate[]>> {
  const response = await contactCenterControllerSyncGupshupConnectorTemplates({ path: { id: connectorId } });
  if (response.error) {
    return normalizeContactCenterApiResult({ error: response.error });
  }
  const templates = normalizeMessageTemplateListResponse(response.data);
  return templates
    ? normalizeContactCenterApiResult({ data: templates })
    : normalizeContactCenterApiResult({ error: { message: 'Template sync returned an invalid response.' } });
}

export async function assignContactCenterConversation(
  id: string,
  assignedTenantMembershipIds: string[],
): Promise<ContactCenterApiResult<ContactCenterConversationDetail>> {
  const result = await contactCenterControllerAssignConversation({
    body: { assignedTenantMembershipIds } as Parameters<typeof contactCenterControllerAssignConversation>[0]['body'],
    path: { conversationId: id },
  });

  return normalizeConversationResponse(result, 'Conversation assignment could not be updated.');
}

export async function linkContactCenterConversationContact(
  id: string,
  contactId: string | null,
): Promise<ContactCenterApiResult<ContactCenterConversationDetail>> {
  const result = await contactCenterControllerLinkConversationContact({
    body: { contactId },
    path: { conversationId: id },
  });

  return normalizeConversationResponse(result, 'Linked contact could not be updated.');
}

export async function linkContactCenterConversationDeal(
  id: string,
  dealId: string | null,
): Promise<ContactCenterApiResult<ContactCenterConversationDetail>> {
  const result = await contactCenterControllerLinkConversationDeal({
    body: { dealId },
    path: { conversationId: id },
  });

  return normalizeConversationResponse(result, 'Linked deal could not be updated.');
}

export async function startContactCenterMetaOAuth(
  kind: ContactCenterMetaOAuthKind,
  connectorId: string,
): Promise<ContactCenterApiResult<ContactCenterMetaOAuthStartResponse>> {
  const token = getBearerToken();
  if (!token) {
    return normalizeContactCenterApiResult({ error: { message: 'Tenant session is required for Meta OAuth setup.', statusCode: 401 } });
  }

  const result = kind === 'facebook'
    ? await facebookOAuthControllerStart({ auth: token, path: { connectorId } })
    : kind === 'instagram-comments'
      ? await instagramCommentsOAuthControllerStart({ auth: token, path: { connectorId } })
      : kind === 'facebook-comments'
        ? await facebookCommentsOAuthControllerStart({ auth: token, path: { connectorId } })
        : await instagramOAuthControllerStart({ auth: token, path: { connectorId } });
  return normalizeContactCenterApiResult(result);
}

export async function reconnectContactCenterMetaOAuth(
  kind: ContactCenterMetaOAuthKind,
  connectorId: string,
): Promise<ContactCenterApiResult<ContactCenterMetaOAuthStartResponse>> {
  const token = getBearerToken();
  if (!token) {
    return normalizeContactCenterApiResult({ error: { message: 'Tenant session is required for Meta OAuth setup.', statusCode: 401 } });
  }

  const result = kind === 'facebook'
    ? await facebookOAuthControllerReconnect({ auth: token, path: { connectorId } })
    : kind === 'instagram-comments'
      ? await instagramCommentsOAuthControllerReconnect({ auth: token, path: { connectorId } })
      : kind === 'facebook-comments'
        ? await facebookCommentsOAuthControllerReconnect({ auth: token, path: { connectorId } })
        : await instagramOAuthControllerReconnect({ auth: token, path: { connectorId } });
  return normalizeContactCenterApiResult(result);
}

export async function completeContactCenterMetaOAuthCallback(
  kind: ContactCenterMetaOAuthKind,
  state: string,
  code: string,
): Promise<ContactCenterApiResult<ContactCenterMetaOAuthCallbackResponse>> {
  if (kind === 'facebook') {
    const result = await facebookOAuthControllerCallback({ query: { state, code } });
    return normalizeContactCenterApiResult<ContactCenterMetaOAuthCallbackResponse>(result);
  }
  if (kind === 'instagram-comments') {
    const result = await instagramCommentsOAuthControllerCallback({ query: { state, code } });
    return normalizeContactCenterApiResult<ContactCenterMetaOAuthCallbackResponse>(result);
  }

  if (kind === 'facebook-comments') {
    const result = await facebookCommentsOAuthControllerCallback({ query: { state, code } });
    return normalizeContactCenterApiResult<ContactCenterMetaOAuthCallbackResponse>(result);
  }

  const result = await instagramOAuthControllerCallback({ query: { state, code } });
  return normalizeContactCenterApiResult<ContactCenterMetaOAuthCallbackResponse>(result);
}

export async function completeContactCenterMetaOAuth(
  kind: ContactCenterMetaOAuthKind,
  connectorId: string,
  input: { session: string; pageId?: string; instagramAccountId?: string },
): Promise<ContactCenterApiResult<unknown>> {
  const token = getBearerToken();
  if (!token) {
    return normalizeContactCenterApiResult({ error: { message: 'Tenant session is required for Meta OAuth setup.', statusCode: 401 } });
  }

  if (kind === 'facebook' || kind === 'facebook-comments') {
    if (!input.pageId) {
      return normalizeContactCenterApiResult({ error: { message: `Choose a ${kind === 'facebook' ? 'Facebook Page' : 'Facebook Comments Page'} before completing OAuth setup.`, statusCode: 400 } });
    }
    if (kind === 'facebook') {
      const result = await facebookOAuthControllerComplete({
        auth: token,
        path: { connectorId },
        body: { session: input.session, pageId: input.pageId },
      });
      return normalizeContactCenterApiResult(result);
    }
    const result = await facebookCommentsOAuthControllerComplete({
      auth: token,
      path: { connectorId },
      body: { session: input.session, pageId: input.pageId },
    });
    return normalizeContactCenterApiResult(result);
  }

  if (!input.instagramAccountId) {
    return normalizeContactCenterApiResult({ error: { message: 'Choose an Instagram account before completing OAuth setup.', statusCode: 400 } });
  }
  const result = kind === 'instagram-comments'
    ? await instagramCommentsOAuthControllerComplete({
        auth: token,
        path: { connectorId },
        body: { session: input.session, instagramAccountId: input.instagramAccountId },
      })
    : await instagramOAuthControllerComplete({
        auth: token,
        path: { connectorId },
        body: { session: input.session, instagramAccountId: input.instagramAccountId },
      });
  return normalizeContactCenterApiResult(result);
}

export async function startContactCenterTikTokBusinessOAuth(
  connectorId: string,
): Promise<ContactCenterApiResult<TiktokBusinessOAuthStartResponseDto>> {
  const token = getBearerToken();
  if (!token) {
    return normalizeContactCenterApiResult({ error: { message: 'Tenant session is required for TikTok Business Messaging OAuth setup.', statusCode: 401 } });
  }

  const result = await tiktokBusinessOAuthControllerStart({
    auth: token,
    path: { connectorId },
  });
  return normalizeContactCenterApiResult(result);
}

export async function completeContactCenterTikTokBusinessOAuth(
  connectorId: string,
  input: { state: string; code: string },
): Promise<ContactCenterApiResult<unknown>> {
  const token = getBearerToken();
  if (!token) {
    return normalizeContactCenterApiResult({ error: { message: 'Tenant session is required for TikTok Business Messaging OAuth setup.', statusCode: 401 } });
  }

  const result = await tiktokBusinessOAuthControllerComplete({
    auth: token,
    path: { connectorId },
    query: { state: input.state, code: input.code },
  });
  return normalizeContactCenterApiResult(result);
}

function normalizeGmailOAuthStartResponse(value: unknown): ContactCenterGmailOAuthStartResponse | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const authorizationUrl = typeof record.authorizationUrl === 'string'
    ? record.authorizationUrl
    : typeof record.url === 'string'
      ? record.url
      : null;
  if (!authorizationUrl) return null;
  return {
    authorizationUrl,
    state: typeof record.state === 'string' ? record.state : undefined,
  };
}

export async function startContactCenterGmailOAuth(
  connectorId: string,
): Promise<ContactCenterApiResult<ContactCenterGmailOAuthStartResponse>> {
  const token = getBearerToken();
  if (!token) {
    return normalizeContactCenterApiResult({ error: { message: 'Tenant session is required for Gmail OAuth setup.', statusCode: 401 } });
  }

  const startResult = await gmailOAuthControllerStart({ auth: token, path: { connectorId } });
  if (startResult.error) {
    const startError = normalizeApiError(startResult.error, 'Gmail OAuth setup could not be started.');
    if (startError.statusCode !== 404) {
      return normalizeContactCenterApiResult({ error: startError });
    }
  }
  const response = startResult.error
    ? await gmailOAuthControllerReconnect({ auth: token, path: { connectorId } })
    : startResult;
  if (response.error) {
    return normalizeContactCenterApiResult({ error: response.error });
  }
  const data = normalizeGmailOAuthStartResponse(response.data);
  return data
    ? normalizeContactCenterApiResult({ data })
    : normalizeContactCenterApiResult({ error: { message: 'Gmail OAuth setup returned an invalid response.' } });
}

export async function completeContactCenterGmailOAuth(
  connectorId: string,
  input: { state: string; code: string },
): Promise<ContactCenterApiResult<unknown>> {
  const token = getBearerToken();
  if (!token) {
    return normalizeContactCenterApiResult({ error: { message: 'Tenant session is required for Gmail OAuth setup.', statusCode: 401 } });
  }

  const response = await gmailOAuthControllerComplete({
    auth: token,
    body: input,
    path: { connectorId },
  });
  return normalizeContactCenterApiResult(response);
}

export async function createContactCenterConnector(draft: ContactCenterConnectorDraft): Promise<ContactCenterApiResult<ContactCenterConnector>> {
  const channelMapResult = await getChannelByIdMap();

  if (channelMapResult.error || !channelMapResult.data) {
    return normalizeContactCenterApiResult({ error: channelMapResult.error ?? { message: 'Contact Center channels could not be loaded.' } });
  }

  const result = await contactCenterControllerCreateConnector({ body: connectorRequest(draft) });

  if (result.error) {
    return normalizeContactCenterApiResult({ error: result.error });
  }

  const connectorResponse = result.data as ContactCenterConnectorResponseDto | undefined;

  if (!connectorResponse?.connector) {
    return normalizeContactCenterApiResult({ error: { message: 'Connector could not be created.' } });
  }

  return normalizeContactCenterApiResult({ data: toConnector(connectorResponse.connector, channelMapResult.data) });
}

export async function updateContactCenterConnector(id: string, draft: ContactCenterConnectorDraft): Promise<ContactCenterApiResult<ContactCenterConnector>> {
  const channelMapResult = await getChannelByIdMap();

  if (channelMapResult.error || !channelMapResult.data) {
    return normalizeContactCenterApiResult({ error: channelMapResult.error ?? { message: 'Contact Center channels could not be loaded.' } });
  }

  const result = await contactCenterControllerUpdateConnector({
    body: connectorRequest(draft),
    path: { id },
  });

  if (result.error) {
    return normalizeContactCenterApiResult({ error: result.error });
  }

  const connectorResponse = result.data as ContactCenterConnectorResponseDto | undefined;

  if (!connectorResponse?.connector) {
    return normalizeContactCenterApiResult({ error: { message: 'Connector could not be updated.' } });
  }

  return normalizeContactCenterApiResult({ data: toConnector(connectorResponse.connector, channelMapResult.data) });
}

export async function getContactCenterConnectorWebhookSetup(id: string): Promise<ContactCenterApiResult<ContactCenterConnector>> {
  const channelMapResult = await getChannelByIdMap();

  if (channelMapResult.error || !channelMapResult.data) {
    return normalizeContactCenterApiResult({ error: channelMapResult.error ?? { message: 'Contact Center channels could not be loaded.' } });
  }

  const result = await contactCenterControllerGetConnectorWebhookSetup({ path: { id } });

  if (result.error) {
    return normalizeContactCenterApiResult({ error: result.error });
  }

  const connectorResponse = result.data as ContactCenterConnectorResponseDto | undefined;

  if (!connectorResponse?.connector) {
    return normalizeContactCenterApiResult({ error: { message: 'Connector webhook setup could not be loaded.' } });
  }

  return normalizeContactCenterApiResult({ data: toConnector(connectorResponse.connector, channelMapResult.data) });
}

export async function regenerateConnectorWebhookToken(id: string): Promise<ContactCenterApiResult<ContactCenterConnector>> {
  const channelMapResult = await getChannelByIdMap();

  if (channelMapResult.error || !channelMapResult.data) {
    return normalizeContactCenterApiResult({ error: channelMapResult.error ?? { message: 'Contact Center channels could not be loaded.' } });
  }

  const result = await contactCenterControllerRegenerateWebhookToken({ path: { id } });

  if (result.error) {
    return normalizeContactCenterApiResult({ error: result.error });
  }

  const connectorResponse = result.data as ContactCenterConnectorResponseDto | undefined;

  if (!connectorResponse?.connector) {
    return normalizeContactCenterApiResult({ error: { message: 'Webhook token could not be regenerated.' } });
  }

  return normalizeContactCenterApiResult({ data: toConnector(connectorResponse.connector, channelMapResult.data) });
}

export async function checkTelegramBusinessConnection(connectorId: string): Promise<ContactCenterApiResult<NonNullable<ContactCenterConnector['settings']['telegramBusinessConnection']>>> {
  const token = getBearerToken();
  if (!token) {
    return normalizeContactCenterApiResult({ error: { message: 'Sign in again to check the Telegram Business connection.', statusCode: 401 } });
  }

  const response = await telegramConnectorAdminControllerCheckBusinessConnection({
    auth: token,
    path: { connectorId },
  });
  if (response.error) {
    return normalizeContactCenterApiResult({ error: response.error });
  }
  const body = response.data as { businessConnection?: unknown } | undefined;
  const businessConnection = normalizeTelegramBusinessConnection(body?.businessConnection);
  return businessConnection
    ? normalizeContactCenterApiResult({ data: businessConnection })
    : normalizeContactCenterApiResult({ error: { message: 'Telegram Business connection check returned an invalid response.' } });
}

function normalizeTelegramBusinessConnection(value: unknown): NonNullable<ContactCenterConnector['settings']['telegramBusinessConnection']> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.id !== 'string' || !record.id.trim()) return null;
  return {
    id: record.id,
    userChatId: typeof record.userChatId === 'string' ? record.userChatId : null,
    userId: typeof record.userId === 'string' ? record.userId : null,
    username: typeof record.username === 'string' ? record.username : null,
    firstName: typeof record.firstName === 'string' ? record.firstName : null,
    lastName: typeof record.lastName === 'string' ? record.lastName : null,
    date: typeof record.date === 'string' ? record.date : null,
    isEnabled: record.isEnabled !== false,
    canReply: record.canReply === true,
    lastUpdateId: typeof record.lastUpdateId === 'number' ? record.lastUpdateId : null,
    checkedAt: typeof record.checkedAt === 'string' ? record.checkedAt : null,
  };
}

export async function archiveContactCenterConnector(id: string): Promise<ContactCenterApiResult<ContactCenterConnector>> {
  const channelMapResult = await getChannelByIdMap();

  if (channelMapResult.error || !channelMapResult.data) {
    return normalizeContactCenterApiResult({ error: channelMapResult.error ?? { message: 'Contact Center channels could not be loaded.' } });
  }

  const result = await contactCenterControllerArchiveConnector({ path: { id } });

  if (result.error) {
    return normalizeContactCenterApiResult({ error: result.error });
  }

  const connectorResponse = result.data as ContactCenterConnectorResponseDto | undefined;

  if (!connectorResponse?.connector) {
    return normalizeContactCenterApiResult({ error: { message: 'Connector could not be archived.' } });
  }

  return normalizeContactCenterApiResult({ data: toConnector(connectorResponse.connector, channelMapResult.data) });
}

export async function restoreContactCenterConnector(id: string): Promise<ContactCenterApiResult<ContactCenterConnector>> {
  const channelMapResult = await getChannelByIdMap();

  if (channelMapResult.error || !channelMapResult.data) {
    return normalizeContactCenterApiResult({ error: channelMapResult.error ?? { message: 'Contact Center channels could not be loaded.' } });
  }

  const result = await contactCenterControllerRestoreConnector({ path: { id } });

  if (result.error) {
    return normalizeContactCenterApiResult({ error: result.error });
  }

  const connectorResponse = result.data as ContactCenterConnectorResponseDto | undefined;

  if (!connectorResponse?.connector) {
    return normalizeContactCenterApiResult({ error: { message: 'Connector could not be restored.' } });
  }

  return normalizeContactCenterApiResult({ data: toConnector(connectorResponse.connector, channelMapResult.data) });
}
